import React from 'react';
import { connect } from 'react-redux';
import * as dates from '@simplybenefits/sb-dates';
import { downloadChangeRequestPdf } from '../../../actions/changeRequests';

import Table from '@mui/material/Table/Table';
import TableHead from '@mui/material/TableHead/TableHead';
import TableRow from '@mui/material/TableRow/TableRow';
import TableCell from '@mui/material/TableCell/TableCell';
import TableBody from '@mui/material/TableBody/TableBody';
import TableContainer from '@mui/material/TableContainer/TableContainer';

import {
    Theme,
    Text,
    Button,
    i18n,
    tokens,
    localizationMaps
} from '@simplybenefits/sb-ui-library';

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const RequestedChangesTable = ({
    requestedChanges,
    loadMoreRequestedChanges,
    dispatch,
    adminMeta,
    employees,
    divisions

}) => {
    const styles = {
        tableHeading: {
            whiteSpace: "nowrap",
            fontSize: Theme.fonts.size.medium,
            fontFamily: Theme.fonts.type.heading,
            backgroundColor: Theme.colors.surface,
            paddingLeft: "2vw",
            color: Theme.colors.mainTextGrey,
            height: '62px',
            border: 'none'
        },
        tableRow: {
            backgroundColor: Theme.colors.surface
        },
        tableCell: {
            height: '62px',
            fontFamily: Theme.fonts.type.light,
            whiteSpace: "nowrap",
            paddingLeft: "2vw",
            color: Theme.colors.mainTextGrey,
            borderTop: `1px solid ${ Theme.colors.lightGrey }`,
            borderBottom: 'none' 

        },
        tableContainer: {
            maxHeight: '620px', 
            boxShadow: `0 0 3px 0 rgba(151, 151, 151, 0.5)`,
            overflowY: 'scroll',
            scrollbarWidth: 'none'
        }
    };

    /**
     * 
     * @param {*} status 
     * @returns 
     */
    const returnStatusText = (status) => {
        if (status.toLowerCase() === "pending admin review") {
            return localizationMaps.fromEnglish('changeRequestStatuses', "Needs Review");
        }

        const lowerStatus = status.toLowerCase();

        if ([ 'incomplete', 'suspended', 'pending agent review', 'pending member review' ].includes(lowerStatus)) {
            return i18n.t(tokens.labels.employees.pending);
        }

        return localizationMaps.fromEnglish('changeRequestStatuses', status);
    };

    /**
     * 
     * @param {*} status 
     */
    const returnStatusColor = (status) => {
        switch (status) {
            case status.toLowerCase().includes('pending'):
                return "#FCE83A";

            case "Approved":
                return Theme.colors.brandGreen;

            case "Declined":
                return Theme.colors.red;
        
            default: 
                return "#FCE83A";
        }
    };

    /**
     * 
     * @param {*} userID 
     * @returns 
     */
    const _writeAccess = (userID) => {
        const divisionID = employees.find((employee) => employee.userID === userID)?.divisionID;
        return divisions[divisionID].access === 'Write' || adminMeta.isParent;
    };

    /**
     * 
     * @param {*} changeRequestID 
     * @param {*} status 
     */
    const _handleDisplay = async (changeRequestData) => {
        const { changeRequestID, status, userID } = changeRequestData;
        const writeAccess = _writeAccess(userID);

        if ([ 'approved', 'declined' ].includes(status.toLowerCase())) {
            const url = await dispatch(downloadChangeRequestPdf(changeRequestID));

            if (typeof url === 'string') {
                window.open(url, '_blank');
            }

            return true;
        }

        // If admin only has read access for the given division only show approved/declined pdfs
        if (!writeAccess) {
            return false;
        }

        window.location.href = `/memberChanges/${ changeRequestID }`;
        return true;
    };

    return (
        <>
            {
                requestedChanges.length ?
                    <>
                        <TableContainer style={ styles.tableContainer }>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.labels.changeRequests.memberName) }
                                        </TableCell>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.labels.changeRequests.identifier) }
                                        </TableCell>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.labels.changeRequests.requestedChange) }
                                        </TableCell>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.labels.changeRequests.dateSubmitted) }
                                        </TableCell>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.labels.admin.effectiveDate) }
                                        </TableCell>
                                        <TableCell style={ styles.tableHeading }>
                                            { i18n.t(tokens.titles.status) }
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {
                                        requestedChanges.map((changeRequestData, i) => (
                                            <React.Fragment key={i}>
                                                <TableRow 
                                                    sx={{
                                                        ...styles.tableRow,
                                                        cursor: 'pointer'
                                                    }} 
                                                    onClick ={ () => _handleDisplay(changeRequestData)}
                                                >
                                                    <TableCell style={{ ...styles.tableCell, fontFamily: Theme.fonts.type.medium }}>
                                                        { changeRequestData.memberName }
                                                    </TableCell>

                                                    <TableCell style={ styles.tableCell}>
                                                        { changeRequestData.changeRequestID }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, fontFamily: Theme.fonts.type.medium }}>
                                                        { localizationMaps.fromEnglish('changeRequestTypes', changeRequestData.type) }
                                                    </TableCell>

                                                    <TableCell style={ styles.tableCell}>
                                                        { dates.formatDateForDisplay(changeRequestData.submittedDate, 'MMM DD, YYYY', i18n.locale) }
                                                    </TableCell>

                                                    <TableCell style={ styles.tableCell}>
                                                        { changeRequestData.effectiveDate ? dates.formatDateForDisplay(changeRequestData.effectiveDate, 'MMM DD, YYYY', i18n.locale) : "-" }
                                                    </TableCell>

                                                    <TableCell style={{ ...styles.tableCell, display: 'flex', alignItems: 'center' }}>
                                                        <Text
                                                            customStyle={{
                                                                fontSize: 20,
                                                                marginRight: 10,
                                                                color: returnStatusColor(changeRequestData.status)
                                                            }}
                                                        >
                                                            •
                                                        </Text>
                                                        { returnStatusText(changeRequestData.status) }
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {
                            requestedChanges.length >= 50
                                ? (
                                    <div 
                                        style={{ 
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'flex-end'
                                        }}
                                    >
                                        <Button
                                            color={"primary"}
                                            customStyle={{
                                                height: 50,
                                                marginRight: 1.25,
                                                marginTop: 1.25,
                                                textTransform: "capitalize",
                                                fontSize: 16,
                                                minWidth: 100
                                            }}
                                            onClick={ loadMoreRequestedChanges }
                                            id="loadmorerequestedchnages"
                                        >
                                            { i18n.t(tokens.buttons.next) }
                                        </Button>
                                    </div>
                                )
                                : null
                        }
                    </>
                    :
                    <Text medium light align="left">{ i18n.t(tokens.labels.changeRequests.noChangeRequestsToDisplay) }</Text>
            }
        </>
    );
};

/**
 * 
 * @param {*} state 
 * @returns 
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    adminMeta: state.admin.meta,
    divisions: state.census.divisions,
    employees: state.census.employees
});

export default connect(mapStateToProps)(RequestedChangesTable);